<style lang="scss" scoped>
  .report {
    &__table {
      &--mobile_wrapper {
        @apply flex overflow-auto w-full #{!important};
      }
      &--vertical {
        @apply relative overflow-hidden #{!important};
      }
    }
  }
</style>

<template>
  <article class="report">
    <header class="report__header">
      <h3 class="section__subtitle">
        Desempenho
      </h3>
    </header>

    <div class="section__wrapper report__wrapper">
      <div
        v-if="filters.length > 0"
        class="report__filters"
      >
        <div
          v-for="(filter, index) in filters"
          :key="`report_filter_${index}`"
          class="report__filters-item"
        >
          <label class="report__filters-label">
            {{ filter.label }}
          </label>

          <template v-if="filter.type === 'date'">
            <div
              v-click-outside="() => filter.showCalendar = false"
              class="report__filters-field"
            >
              <div
                :class="filter.showCalendar && 'active'"
                class="report__filters-date"
                @click="filter.showCalendar = !filter.showCalendar"
              >
                <span>
                  {{ $moment(filter.value).format('DD/MM/YYYY') }}
                </span>

                <dr-icon
                  :color="'#8E8E92'"
                  class="agenda-settings__date-picker--icon"
                  name="dashCalendar"
                />
              </div>

              <div class="report__filters-calendar">
                <dr-calendar
                  v-show="filter.showCalendar"
                  v-model="filter.value"
                  @change="filter.showCalendar = false"
                />
              </div>
            </div>
          </template>

          <template v-if="filter.type === 'text'">
            <div class="report__filters-field">
              <input
                v-model.lazy="filter.value"
                :placeholder="filter.placeholder"
                type="text"
                class="report__filters-text"
              >

              <i
                v-show="filter.value"
                class="icon icon-cross report__filters-reset"
                @click="filter.value = null"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="report__body">
        <dr-loader
          v-if="ui.isLoading"
          size="lg"
          class="report__loader"
        />

        <p
          v-if="!ui.isLoading && Array.isArray(data.rows) && data.rows.length === 0"
          class="report__no-results"
        >
          Nenhum resultado encontrado.
        </p>

        <div
          v-if="!ui.isLoading && Array.isArray(data.rows) && data.rows.length > 0"
          class="report__table report__table--vertical"
        >
          <div class="report__table-column report__table-column--header">
            <div
              v-for="(key, index) in data.keys"
              :key="`report_header_row_${index}`"
              class="report__table-line report__table-line--header"
            >
              <div class="report__table-title">
                <span>
                  {{ key.title || '&nbsp;' }}
                </span>

                <dr-tooltip
                  v-if="key.tooltip"
                  class="report__table-tooltip"
                  :text="key.tooltip"
                />
              </div>
            </div>
          </div>
          <div class="report__table--mobile_wrapper">
            <div
              v-for="(column, index) in data.rows"
              :key="`report_data_col_${index}`"
              class="report__table-column"
            >
              <div
                v-for="(cell, i) in column"
                :key="`report_data_col_${index}_cell_${i}`"
                :class="cell.class"
                class="report__table-line"
              >
                <span v-if="cell.name === 'month'">
                  {{ $moment(cell.value, 'YYYY-MM', true).format('MMMM/YYYY') }}
                </span>

                <span v-else-if="cell.name === 'receita'">
                  {{ cell.value | TO_MONEY }}
                </span>

                <span v-else-if="cell.name === 'despesa'">
                  {{ cell.value > 0 ? '-' : '' }}
                  {{ cell.value | TO_MONEY }}
                </span>

                <span
                  v-else-if="cell.name === 'lucro-prejuizo'"
                  :class="[
                    cell.value > 0 ? 'report__table-line--blue' : '',
                    cell.value < 0 ? 'report__table-line--red' : ''
                  ]"
                >
                  {{ cell.value >= 0 ? '' : '-' }}
                  {{ cell.value | REMOVE_HIPHEN | TO_MONEY }}
                </span>

                <span v-else>
                  {{ cell.value }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer
      v-if="ui.hasLoaded && pagination.totalPages > 1"
      class="section__footer"
    >
      <nav class="pagination">
        <transition name="fade" mode="out-in">
          <dr-table-pagination
            :total-pages.sync="pagination.totalPages"
            :current-page.sync="pagination.currentPage"
            class="text-right"
            @updated="getReport($event)"
          />
        </transition>
      </nav>
    </footer>
  </article>
</template>

<script>
import DrTablePagination from '@/components/tables/tablePagination'
import DrTooltip from '@/components/tooltip'
import { DrCalendar } from 'cm2tech-vue-calendar'
import { mapGetters } from 'vuex'

export default {
  name: 'DrReportsPerformance',

  filters: {
    'REMOVE_HIPHEN': val => String(val).replace('-', '')
  },

  components: {
    DrTablePagination,
    DrCalendar,
    DrTooltip
  },

  data () {
    return {
      data: {
        keys: [
          { title: '' },
          {
            title: 'Receitas',
            tooltip: 'Entradas recebidas no extrato do mês'
          },
          {
            title: 'Despesas',
            tooltip: 'Gastos no extrato do mês'
          },
          {
            title: 'Lucro/Prejuízo',
            tooltip: 'Receitas menos as despesas'
          },
          {
            title: 'Inadimplência',
            tooltip: 'Valor de consultas não pagas no mês'
          },
          {
            title: 'Lucratividade',
            tooltip: 'Percental de lucros/prejuízos do mês'
          }
        ],
        rows: null
      },
      filters: [],
      pagination: {
        totalRows: null,
        totalPages: null,
        currentPage: null
      },
      itemsPerPage: 10,
      ui: {
        isLoading: true,
        hasLoaded: false
      }
    }
  },

  computed: {
    ...mapGetters({
      office: 'getCurrentOffice'
    }),

    endpoint () {
      return `/report/doctor_office/${this.office.id}/performance`
    },

    queryString () {
      return ''
    }
  },

  watch: {
    filters: {
      deep: true,
      immediate: false,
      handler (filters) {
        const startDate = filters.find(f => f.name === 'startDate')
        const endDate = filters.find(f => f.name === 'endDate')

        if (this.$moment(startDate.value).isAfter(this.$moment(endDate.value), 'day')) {
          this.filters.find(f => f.name === 'endDate').value = startDate.value
        }

        if (this.$moment(endDate.value).isBefore(this.$moment(startDate.value), 'day')) {
          this.filters.find(f => f.name === 'startDate').value = endDate.value
        }
      }
    },

    queryString: {
      deep: false,
      immediate: false,
      handler (next, prev) {
        if (prev !== next) {
          this.getReport()
        }
      }
    },

    'office.id': {
      deep: false,
      immediate: false,
      handler () {
        this.getReport()
      }
    }
  },

  created () {
    this.getReport()
  },

  methods: {
    getReport () {
      const url = `${this.endpoint}?${this.queryString}`

      this.ui.isLoading = true
      this.fn_SHOW_PROGRESS()
      this.$http
        .get(url, this.$store.getters.getAuthHeader)
        .then((res) => {
          const data = res.data
          const months = Object.entries(data)
          const handledData = []

          months.forEach((item) => {
            const keys = item[1]
            const column = []
            column.push({ name: 'month', value: item[0] })

            Object.entries(keys).forEach((k) => {
              column.push({
                name: this.fn_REMOVE_ACCENTS(String(k[0]).toLowerCase().replace('/', '-')),
                value: k[1]
              })
            })

            handledData.push(column)
          })

          this.data.rows = handledData
            .map(this.sortColumns)
            .map(this.removeNegative)
        })
        .finally(() => {
          this.ui.isLoading = false
          this.ui.hasLoaded = true
          this.fn_HIDE_PROGRESS()
        })
    },

    sortColumns (arr = []) {
      const sortedArr = []

      if (arr.length > 0) {
        sortedArr.push({ ...arr.find(i => i.name === 'month'), class: 'report__table-line--month' })
        sortedArr.push({ ...arr.find(i => i.name === 'receita'), class: 'report__table-line--receita' })
        sortedArr.push({ ...arr.find(i => i.name === 'despesa'), class: 'report__table-line--despesa' })
        sortedArr.push({ ...arr.find(i => i.name === 'lucro-prejuizo') })
        sortedArr.push({ ...arr.find(i => i.name === 'inadimplencia') })
        sortedArr.push({ ...arr.find(i => i.name === 'lucratividade') })
      }

      return sortedArr
    },

    removeNegative (arr = []) {
      if (arr.length > 0) {
        arr.forEach(item => {
          if (item.name === 'receita') {
            item.value = parseFloat(item.value)
          }

          if (item.name === 'despesa') {
            item.value = parseFloat(String(item.value).replace('-', ''))
          }
        })
      }

      return arr
    }
  }
}
</script>
